import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  TaskSearchRequest,
  CreateTaskRequest,
  UpdateTaskRequest,
  TaskInboxSearchRequest,
  TaskCountsRequest,
} from '@vagusx/clients';
import { getTaskClient } from '@/lib/api-clients';
import { queryKeys } from '@/lib/query-keys';

/**
 * Hook to fetch and search tasks.
 */
export function useTasks(searchRequest?: TaskSearchRequest) {
  return useQuery({
    queryKey: queryKeys.tasks.list(searchRequest),
    queryFn: () => getTaskClient().search(searchRequest ?? {}),
  });
}

/**
 * Hook to fetch a single task by ID.
 */
export function useTask(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.tasks.detail(id ?? ''),
    queryFn: () => getTaskClient().getById(id!),
    enabled: !!id,
  });
}

/**
 * Hook to fetch task history.
 */
export function useTaskHistory(taskId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.tasks.history(taskId ?? ''),
    queryFn: () => getTaskClient().getHistory(taskId!),
    enabled: !!taskId,
  });
}

/**
 * Hook to fetch tasks for a specific interaction.
 */
export function useTasksByInteraction(interactionId: string | undefined) {
  return useQuery({
    queryKey: [...queryKeys.tasks.all, 'interaction', interactionId] as const,
    queryFn: () => getTaskClient().getByInteractionId(interactionId!),
    enabled: !!interactionId,
  });
}

/**
 * Hook to create a task.
 */
export function useCreateTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateTaskRequest) => getTaskClient().create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tasks.all });
      // Also invalidate interactions list to update task metadata
      queryClient.invalidateQueries({ queryKey: queryKeys.interactions.all });
    },
  });
}

/**
 * Hook to update a task.
 */
export function useUpdateTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateTaskRequest }) =>
      getTaskClient().update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tasks.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.tasks.detail(id) });
    },
  });
}

/**
 * Hook to complete a task.
 */
export function useCompleteTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => getTaskClient().changeStatus(id, 'completed'),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tasks.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.tasks.detail(id) });
    },
  });
}

/**
 * Hook to cancel a task.
 */
export function useCancelTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => getTaskClient().changeStatus(id, 'cancelled'),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tasks.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.tasks.detail(id) });
    },
  });
}

/**
 * Hook to bulk-mark a set of tasks as completed.
 *
 * Per-row best-effort on the server: returns
 * `{ completed: string[], failed: [{ id, reason }] }`. Caller wires
 * its own toast/dialog from the response — this hook just owns the
 * fetch + cache invalidation.
 *
 * No optimistic update at the cache layer because the response shape
 * is "partial-success" by design. Rolling individual rows in/out of
 * an optimistically-mutated list is more code than the perceived-
 * latency win is worth for a bulk action that already shows a toast.
 * The post-success `invalidateQueries` triggers a refetch and the
 * completed rows leave the Open filter naturally.
 */
export function useBulkCompleteTasks() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => getTaskClient().bulkComplete(ids),
    onSuccess: () => {
      // Invalidate only tasks. Task-status changes don't alter
      // interaction shape, so invalidating `interactions.all` would
      // just burn network for no payoff.
      queryClient.invalidateQueries({ queryKey: queryKeys.tasks.all });
    },
  });
}

/**
 * Hook to assign a task to a user or team.
 */
export function useAssignTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      assigneeType,
      assigneeId,
    }: {
      id: string;
      assigneeType: 'user' | 'team';
      assigneeId: string;
    }) => getTaskClient().assign(id, assigneeType, assigneeId),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tasks.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.tasks.detail(id) });
    },
  });
}

/**
 * Hook to unassign a task.
 */
export function useUnassignTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => getTaskClient().unassign(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tasks.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.tasks.detail(id) });
    },
  });
}

/**
 * Hook to add a comment to a task.
 */
export function useAddTaskComment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, comment }: { id: string; comment: string }) =>
      getTaskClient().addComment(id, comment),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tasks.history(id) });
    },
  });
}

/**
 * Hook to search tasks for inbox view (enriched with email/document extraction data).
 */
export function useTaskInbox(params: TaskInboxSearchRequest, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.tasks.inbox(params),
    queryFn: () => getTaskClient().searchInbox(params),
    refetchInterval: 5 * 60 * 1000, // Auto-refresh every 5 minutes
    enabled: options?.enabled,
  });
}

/**
 * Hook for the inbox header — per-scope tab counts and per-label chip
 * counts within the active scope. Single endpoint round-trip; auto-
 * refreshes alongside the inbox list and on every task mutation
 * (which already invalidate `tasks.all`).
 */
export function useTaskCounts(params: TaskCountsRequest, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.tasks.counts(params),
    queryFn: () => getTaskClient().getInboxCounts(params),
    refetchInterval: 5 * 60 * 1000,
    enabled: options?.enabled,
  });
}

/**
 * Hook to mark a task as read.
 */
export function useMarkTaskRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => getTaskClient().markRead(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tasks.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.tasks.detail(id) });
    },
  });
}

/**
 * Hook to mark a task as unread.
 */
export function useMarkTaskUnread() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => getTaskClient().markUnread(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tasks.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.tasks.detail(id) });
    },
  });
}

/**
 * Hook to star a task.
 */
export function useStarTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => getTaskClient().markStarred(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tasks.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.tasks.detail(id) });
    },
  });
}

/**
 * Hook to unstar a task.
 */
export function useUnstarTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => getTaskClient().markUnstarred(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tasks.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.tasks.detail(id) });
    },
  });
}

/**
 * Hook to reopen a task (change status back to open).
 */
export function useReopenTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => getTaskClient().changeStatus(id, 'open'),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tasks.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.tasks.detail(id) });
    },
  });
}
